import React, { useEffect, useMemo, useRef, useState } from 'react';
import Backdrop from '@mui/material/Backdrop';
import { ensureGoogleTextFontsLoaded } from './textFonts.config';
import { isGoogleTextFontFamily } from './fontFamilyRegistry';
import {
  TEXT_TEMPLATE_CATEGORIES,
  getTextTemplateCanvasStyle,
  templateUsesPreviewCard,
  type TextTemplateCategory,
  type TextTemplateItem,
} from './textTemplateCatalog';
import type { UiTheme } from './theme/uiTheme';

function CloseIcon() {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth={0}
      viewBox="0 0 1024 1024"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth={0}
      viewBox="0 0 24 24"
      height="1em"
      width="1em"
      aria-hidden
    >
      <path d="M10,18c1.846,0,3.543-0.635,4.897-1.688l4.396,4.396l1.414-1.414l-4.396-4.396C17.365,13.543,18,11.846,18,10 c0-4.411-3.589-8-8-8s-8,3.589-8,8S5.589,18,10,18z M10,4c3.309,0,6,2.691,6,6s-2.691,6-6,6s-6-2.691-6-6S6.691,4,10,4z" />
    </svg>
  );
}

const PREVIEW_SCALE_DEFAULT = 1.6;
const ALL_CATEGORIES_FILTER = '';

const templateSearchHaystack = (item: TextTemplateItem, category: TextTemplateCategory) => {
  const style = item.style;
  const parts = [
    item.label,
    String(style.text ?? ''),
    category.title,
    category.description ?? '',
    String(style.fontFamily ?? ''),
    ...(item.keywords ?? []),
  ];
  return parts.join(' ').toLowerCase();
};

type PreviewProps = {
  item: TextTemplateItem;
  uiTheme: UiTheme;
};

function TextTemplatePreview({ item, uiTheme }: PreviewProps) {
  const resolved = getTextTemplateCanvasStyle(uiTheme, item);
  const scale = item.previewScale ?? PREVIEW_SCALE_DEFAULT;
  const fontSize = Number(resolved.fontSize) || 24;
  const previewSize = Math.max(12, Math.min(36, fontSize / scale));
  const fill = item.themeAdaptiveFill
    ? 'var(--theme-overlay-text)'
    : (resolved.fill as string) || 'var(--theme-overlay-text)';

  const shadow =
    resolved.shadowBlur != null && Number(resolved.shadowBlur) > 0
      ? `${resolved.shadowOffsetX ?? 0}px ${resolved.shadowOffsetY ?? 0}px ${resolved.shadowBlur}px ${resolved.shadowColor ?? 'transparent'}`
      : undefined;

  return (
    <span
      className="respark_text-template-preview__label"
      style={{
        fontFamily: String(resolved.fontFamily ?? 'Poppins'),
        fontSize: previewSize,
        fontStyle: (resolved.fontStyle as string) || 'normal',
        fontWeight: resolved.fontStyle === 'bold' ? 700 : undefined,
        color: fill,
        textAlign: (resolved.align as React.CSSProperties['textAlign']) || 'center',
        letterSpacing: resolved.letterSpacing as number | undefined,
        lineHeight: resolved.lineHeight as number | undefined,
        WebkitTextStroke:
          resolved.strokeWidth && Number(resolved.strokeWidth) > 0
            ? `${Math.min(2, Number(resolved.strokeWidth))}px ${resolved.stroke}`
            : undefined,
        textShadow: shadow,
      }}
    >
      {item.label}
    </span>
  );
}

export type TextTemplatePickerModalProps = {
  open: boolean;
  uiTheme: UiTheme;
  onClose: () => void;
  onSelect: (template: TextTemplateItem) => void;
  contentRef?: React.RefObject<HTMLDivElement | null>;
};

export default function TextTemplatePickerModal({
  open,
  uiTheme,
  onClose,
  onSelect,
  contentRef,
}: TextTemplatePickerModalProps) {
  const [query, setQuery] = useState('');
  const [categoryFilterId, setCategoryFilterId] = useState(ALL_CATEGORIES_FILTER);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setCategoryFilterId(ALL_CATEGORIES_FILTER);
      return;
    }
    ensureGoogleTextFontsLoaded();
    const t = window.setTimeout(() => searchRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [open]);

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    const scope = categoryFilterId
      ? TEXT_TEMPLATE_CATEGORIES.filter((category) => category.id === categoryFilterId)
      : TEXT_TEMPLATE_CATEGORIES;

    if (!q) {
      return scope.filter((category) => category.templates.length > 0);
    }

    return scope
      .map((category) => ({
        ...category,
        templates: category.templates.filter((item) =>
          templateSearchHaystack(item, category).includes(q),
        ),
      }))
      .filter((category) => category.templates.length > 0);
  }, [query, categoryFilterId]);

  const totalMatches = filteredCategories.reduce((n, c) => n + c.templates.length, 0);

  const handleBackdropClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('respark_text-template-modal')) return;
    const panel = contentRef?.current;
    if (panel && !panel.contains(target)) onClose();
  };

  const handleSelect = (item: TextTemplateItem) => {
    onSelect(item);
    onClose();
  };

  return (
    <Backdrop
      className="backdrop-modal-wrapper respark_text-template-modal"
      open={open}
      onClick={handleBackdropClick}
    >
      <div
        className={`backdrop-modal-content respark_text-template-modal__panel respark_text-template-modal__panel--${uiTheme}`}
        ref={contentRef as React.RefObject<HTMLDivElement>}
        role="dialog"
        aria-modal="true"
        aria-labelledby="respark-text-template-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="respark_text-template-modal__header">
          <div className="respark_text-template-modal__header-top">
            <div className="respark_text-template-modal__title-wrap">
              <h2 id="respark-text-template-title" className="heading">
                Add text to design
              </h2>
              <p className="respark_text-template-modal__subtitle">
                Salon, spa & clinic styles — edit on the canvas
              </p>
            </div>
            <button
              type="button"
              className="modal-close respark_text-template-modal__close"
              aria-label="Close"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="respark_text-template-modal__toolbar">
            <label className="respark_text-template-modal__search-field">
              <SearchIcon />
              <input
                ref={searchRef}
                type="search"
                className="respark_text-template-modal__search-input"
                placeholder="Search styles, emoji, or fonts…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <span className="respark_text-template-modal__sr-only">Search text templates</span>
            </label>
            <select
              className="respark_text-template-modal__category-filter"
              value={categoryFilterId}
              onChange={(e) => setCategoryFilterId(e.target.value)}
              aria-label="Filter by category"
            >
              <option value={ALL_CATEGORIES_FILTER}>All categories</option>
              {TEXT_TEMPLATE_CATEGORIES.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.title}
                </option>
              ))}
            </select>
          </div>
        </header>

        <div className="modal-containt respark_text-template-modal__body">
          {totalMatches === 0 ? (
            <p className="respark_text-template-modal__empty">No styles match your search or category.</p>
          ) : (
            filteredCategories.map((category) => (
              <section
                key={category.id}
                className="respark_text-template-category"
                aria-labelledby={`text-cat-${category.id}`}
              >
                <div className="respark_text-template-category__head">
                  <h3 id={`text-cat-${category.id}`} className="respark_text-template-category__title">
                    {category.title}
                  </h3>
                  {category.description ? (
                    <p className="respark_text-template-category__desc">{category.description}</p>
                  ) : null}
                </div>
                <ul className="respark_text-template-grid">
                  {category.templates.map((item) => {
                    const isEffect = templateUsesPreviewCard(item);
                    const needsGoogle = isGoogleTextFontFamily(String(item.style.fontFamily ?? ''));
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          className={`respark_text-template-tile${isEffect ? ' respark_text-template-tile--effect' : ''}`}
                          onClick={() => handleSelect(item)}
                          title={item.label}
                        >
                          <TextTemplatePreview item={item} uiTheme={uiTheme} />
                          {needsGoogle ? (
                            <span className="respark_text-template-tile__sr-only">
                              {String(item.style.fontFamily)}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </Backdrop>
  );
}
